class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    // Elexa: adds go through Elexa.cart, which doesn't call setActiveElement, so remember the control
    // the shopper used (the add-to-cart button) and give focus back to it on close.
    const current = document.activeElement;
    if (current && current !== document.body && !this.contains(current)) this.setActiveElement(current);
    this.notification.classList.add('animate', 'active');

    // Elexa: only the popup's own slide counts (a child's transition could end first), with a timer
    // for reduced motion / no transition.
    if (this.onOpenEnd) this.notification.removeEventListener('transitionend', this.onOpenEnd);
    clearTimeout(this.openFocusTimer);
    this.onOpenEnd = (evt) => {
      if (evt && evt.target !== this.notification) return;
      this.notification.removeEventListener('transitionend', this.onOpenEnd);
      clearTimeout(this.openFocusTimer);
      if (!this.notification.classList.contains('active')) return;
      this.notification.focus();
      trapFocus(this.notification);
    };
    this.notification.addEventListener('transitionend', this.onOpenEnd);
    this.openFocusTimer = setTimeout(() => this.onOpenEnd(), 700);

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    clearTimeout(this.openFocusTimer);
    if (this.onOpenEnd) this.notification.removeEventListener('transitionend', this.onOpenEnd);
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    const cartApi = window.Elexa && window.Elexa.cart;
    if (cartApi && typeof cartApi.markExternalRender === 'function') cartApi.markExternalRender();
    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections && parsedState.sections[section.id];
      if (!html) return;
      // Elexa: the drawer section carries the cart state and the popup upsells.
      if (section.id === 'cart-drawer') {
        if (cartApi && cartApi.renderers && cartApi.renderers['cart-drawer']) cartApi.renderers['cart-drawer'](html, {});
        return;
      }
      const target = document.getElementById(section.id);
      if (target) target.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
    if (document.getElementById('cart-notification-upsells')) sections.push({ id: 'cart-drawer' });
    return sections;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
